import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CollectionHeader = ({ collection, itemCount, onRename, onDelete, onShare, onBack }) => {
  const getCollectionIcon = (type) => {
    switch (type) {
      case 'vintage-watches': return 'Watch';
      case 'sports-cards': return 'CreditCard';
      case 'comics': return 'Book';
      case 'art-prints': return 'Image';
      case 'stamps': return 'Mail';
      case 'rare-coins': return 'Coins';
      case 'sneakers': return 'ShoppingBag';
      case 'toys': return 'Gamepad2';
      default: return 'Package';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="mb-8">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-1 hover:text-foreground transition-colors"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Collections</span>
        </button>
        <Icon name="ChevronRight" size={16} />
        <span className="text-foreground font-medium capitalize">
          {collection?.collectible_type?.replace('-', ' ')}
        </span>
        <Icon name="ChevronRight" size={16} />
        <span className="text-foreground">{collection?.name}</span>
      </div>
      {/* Header Content */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {/* Collection Icon */}
            <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon 
                name={getCollectionIcon(collection?.collectible_type)} 
                size={32} 
                className="text-primary" 
              />
            </div>

            {/* Collection Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-card-foreground">
                  {collection?.name}
                </h1>
                
                {/* Collection Type Badge */}
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary capitalize">
                  {collection?.collectible_type?.replace('-', ' ')}
                </span>

                {/* Public/Private Badge */}
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  collection?.is_public 
                    ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  <Icon 
                    name={collection?.is_public ? 'Eye' : 'EyeOff'} 
                    size={12} 
                    className="mr-1" 
                  />
                  {collection?.is_public ? 'Public' : 'Private'}
                </span>
              </div>

              {/* Description */}
              {collection?.description && (
                <p className="text-muted-foreground mb-4 max-w-2xl">
                  {collection?.description}
                </p>
              )}

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Package" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Items</span>
                  </div>
                  <div className="text-2xl font-bold text-card-foreground">
                    {itemCount || 0}
                  </div>
                </div>

                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Total Value</span>
                  </div>
                  <div className="text-2xl font-bold text-success">
                    ${(parseFloat(collection?.total_value) || 0)?.toLocaleString()}
                  </div>
                </div>

                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Calendar" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Created</span>
                  </div>
                  <div className="text-sm font-medium text-card-foreground">
                    {formatDate(collection?.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onRename}
              iconName="Edit2"
              iconPosition="left"
            >
              Rename
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
              iconName="Share2"
              iconPosition="left"
            >
              Share
            </Button>
            
            <Button
              variant="destructive"
              size="sm"
              onClick={onDelete}
              iconName="Trash2"
              iconPosition="left"
            >
              Delete
            </Button>
          </div>
        </div>

        {/* Performance Metrics (if available) */}
        {collection?.total_value > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Avg. Item Value</div>
                <div className="font-semibold text-card-foreground">
                  ${Math.round((parseFloat(collection?.total_value) || 0) / Math.max(itemCount, 1))?.toLocaleString()}
                </div>
              </div>
              
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Collection Growth</div>
                <div className="font-semibold text-success flex items-center justify-center">
                  <Icon name="TrendingUp" size={14} className="mr-1" />
                  +12.5%
                </div>
              </div>

              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Last Updated</div>
                <div className="font-semibold text-card-foreground">
                  {formatDate(collection?.updated_at)}
                </div>
              </div>

              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Completion</div>
                <div className="font-semibold text-primary">
                  {Math.min(100, Math.round((itemCount / 50) * 100))}%
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHeader;